import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Car Maintenance",
  "Battery Installation",
  "Lockout Service",
  "Tire Changes",
  "Oil Changes",
  "Gas Delivery",
];

const initialForm = {
  name: "",
  phone: "",
  email: "",
  service: "",
  preferred_date: "",
  location: "",
  message: "",
};

const fieldClass =
  "rounded-none border-0 border-b border-white/20 bg-transparent px-0 text-white placeholder:text-white/30 focus-visible:border-primary focus-visible:ring-0 focus-visible:ring-offset-0";

export default function ScheduleService() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      location: form.location.trim(),
      message: form.message.trim(),
      preferred_date: form.preferred_date.trim(),
    };
    if (!payload.name || !payload.phone || !payload.email || !payload.service) {
      toast.error("Please fill in name, phone, email and service.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/service-requests`, payload);
      if (res.data?.email_sent) {
        toast.success("Request sent! We'll be in touch shortly.");
      } else {
        toast.success("Request received! We'll call you back shortly.");
      }
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call us at 504-435-2757.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="schedule"
      data-testid="schedule-section"
      className="relative border-t border-white/10 bg-[#0A0A0A] px-6 py-28 md:px-12 md:py-40"
    >
      <div className="mx-auto grid max-w-[1400px] gap-16 md:grid-cols-2 md:gap-24">
        {/* Left */}
        <div>
          <p className="overline mb-5 flex items-center gap-3 text-primary">
            <span className="h-px w-10 bg-primary" /> Get Rolling
          </p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="font-display text-5xl leading-[0.95] text-white sm:text-6xl lg:text-7xl"
          >
            REQUEST <br />
            <span className="text-primary">ASSISTANCE</span>
          </motion.h2>
          <p className="mt-8 max-w-md text-lg leading-relaxed text-white/60">
            Tell us what happened and where you are. We&apos;ll get a crew heading
            your way as fast as possible.
          </p>

          <div className="mt-12 space-y-6">
            <a
              href="tel:5044352757"
              data-testid="contact-phone"
              className="group flex items-center gap-4 text-white transition-colors hover:text-primary"
            >
              <span className="flex h-11 w-11 items-center justify-center border border-white/15 group-hover:border-primary">
                <Phone className="h-5 w-5 text-primary" />
              </span>
              <span className="text-lg">504-435-2757</span>
            </a>
            <a
              href="mailto:stormsrs@outlook.com"
              data-testid="contact-email"
              className="group flex items-center gap-4 text-white transition-colors hover:text-primary"
            >
              <span className="flex h-11 w-11 items-center justify-center border border-white/15 group-hover:border-primary">
                <Mail className="h-5 w-5 text-primary" />
              </span>
              <span className="text-lg">stormsrs@outlook.com</span>
            </a>
            <div className="flex items-center gap-4 text-white">
              <span className="flex h-11 w-11 items-center justify-center border border-white/15">
                <MapPin className="h-5 w-5 text-primary" />
              </span>
              <span className="text-lg">3327 Pontchatrain Dr. Slidell, LA 70458</span>
            </div>
          </div>
        </div>

        {/* Right - Form */}
        <motion.form
          data-testid="schedule-form"
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col gap-8 border border-white/10 bg-[#111111] p-8 md:p-10"
        >
          <div className="grid gap-8 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="name" className="overline text-white/50">Full Name *</Label>
              <Input id="name" data-testid="input-name" value={form.name} onChange={update("name")} placeholder="John Doe" className={fieldClass} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="phone" className="overline text-white/50">Phone *</Label>
              <Input id="phone" data-testid="input-phone" value={form.phone} onChange={update("phone")} placeholder="504-000-0000" className={fieldClass} />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="email" className="overline text-white/50">Email *</Label>
            <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={update("email")} placeholder="you@email.com" className={fieldClass} />
          </div>

          <div className="grid gap-8 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label className="overline text-white/50">Service Needed *</Label>
              <Select value={form.service} onValueChange={update("service")}>
                <SelectTrigger data-testid="select-service" className="rounded-none border-0 border-b border-white/20 bg-transparent px-0 text-white focus:ring-0 focus:ring-offset-0 data-[placeholder]:text-white/30">
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent className="rounded-none border-white/15 bg-[#111111] text-white">
                  {SERVICES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`service-option-${s.replace(/\s+/g, "-").toLowerCase()}`} className="rounded-none focus:bg-primary focus:text-white">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="date" className="overline text-white/50">Preferred Date/Time</Label>
              <Input id="date" data-testid="input-date" value={form.preferred_date} onChange={update("preferred_date")} placeholder="ASAP / Tomorrow 9am" className={fieldClass} />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="location" className="overline text-white/50">Your Location</Label>
            <Input id="location" data-testid="input-location" value={form.location} onChange={update("location")} placeholder="Address or nearest cross-street" className={fieldClass} />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="message" className="overline text-white/50">Details</Label>
            <Textarea id="message" data-testid="input-message" value={form.message} onChange={update("message")} placeholder="Describe the issue..." rows={3} className={`${fieldClass} resize-none`} />
          </div>

          <button
            type="submit"
            data-testid="submit-request-btn"
            disabled={submitting}
            className="group mt-2 flex items-center justify-center gap-3 bg-primary py-4 text-sm font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Sending
              </>
            ) : (
              "Send Request"
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
